import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, Skeleton, TextField } from "../components/ui";
import { deletePantryItem, fetchPantryItems, updatePantryItem } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { assetUrl } from "../lib/assets";
import { addItem, loadShoppingList, saveShoppingList } from "../lib/shoppingList";
import type { PantryItem } from "../types";

function normalizeCategory(value?: string | null): string {
  return (value || "Uncategorized").trim() || "Uncategorized";
}

export function PantryPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<PantryItem[]>([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [pendingItemId, setPendingItemId] = useState<string | null>(null);
  const [shoppingFeedback, setShoppingFeedback] = useState<{ id: string; message: string } | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const shoppingFeedbackTimerRef = useRef<ReturnType<typeof setTimeout>>();

  function handleSearchChange(value: string): void {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const trimmed = value.trim();
    if (trimmed.length >= 2) {
      searchTimerRef.current = setTimeout(() => {
        trackEvent("pantry_searched", { query: trimmed });
      }, 600);
    }
  }

  function handleCategoryChange(value: string): void {
    setSelectedCategory(value);
    trackEvent("pantry_category_filtered", { category: value });
  }

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      let cursor: string | null | undefined = null;
      const aggregated: PantryItem[] = [];

      // Pull a few pages to match iOS grouped browsing expectations.
      for (let i = 0; i < 3; i += 1) {
        const response = await fetchPantryItems(50, cursor ?? undefined);
        aggregated.push(...(response.items ?? []));
        cursor = response.next_cursor;
        if (!cursor) {
          break;
        }
      }

      setItems(aggregated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load pantry items.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const categories = useMemo(() => {
    const values = new Set(items.map((item) => normalizeCategory(item.category)));
    return ["All", ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [items]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const name = item.item_name.toLowerCase();
      const category = normalizeCategory(item.category);
      const matchesCategory = selectedCategory === "All" || category === selectedCategory;
      const matchesSearch = !query || name.includes(query) || category.toLowerCase().includes(query);
      return matchesCategory && matchesSearch;
    });
  }, [items, search, selectedCategory]);

  async function handleDelete(item: PantryItem): Promise<void> {
    const pantryItemId = item.pantry_item_id ?? item.pantry_item_ids?.[0];
    if (!pantryItemId) {
      setError("Item is missing pantry_item_id and cannot be deleted.");
      return;
    }

    const confirmed = window.confirm(`Delete ${item.item_name}?`);
    if (!confirmed) {
      return;
    }

    setPendingItemId(pantryItemId);
    setError(null);
    try {
      await deletePantryItem(pantryItemId);
      setItems((current) => current.filter((candidate) => (candidate.pantry_item_id ?? candidate.pantry_item_ids?.[0]) !== pantryItemId));
      trackEvent("pantry_item_deleted", { category: normalizeCategory(item.category) });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete pantry item.");
    } finally {
      setPendingItemId(null);
    }
  }

  function handleAddToShoppingList(item: PantryItem): void {
    const itemId = item.pantry_item_id ?? item.pantry_item_ids?.[0] ?? item.submission_id;
    const current = loadShoppingList();
    const next = addItem(current, {
      name: item.item_name,
      category: item.category,
      source: "pantry",
    });
    saveShoppingList(next);
    trackEvent("pantry_added_to_shopping_list", { category: normalizeCategory(item.category) });
    if (shoppingFeedbackTimerRef.current) {
      clearTimeout(shoppingFeedbackTimerRef.current);
    }
    setShoppingFeedback({ id: itemId, message: "Added to shopping list" });
    shoppingFeedbackTimerRef.current = setTimeout(() => setShoppingFeedback(null), 2500);
  }

  async function handleRename(item: PantryItem): Promise<void> {
    const pantryItemId = item.pantry_item_id ?? item.pantry_item_ids?.[0];
    if (!pantryItemId) {
      setError("Item is missing pantry_item_id and cannot be updated.");
      return;
    }

    const nextName = window.prompt("Rename pantry item", item.item_name)?.trim();
    if (!nextName || nextName === item.item_name) {
      return;
    }

    setPendingItemId(pantryItemId);
    setError(null);
    try {
      await updatePantryItem(pantryItemId, { item_name: nextName });
      setItems((current) =>
        current.map((candidate) =>
          (candidate.pantry_item_id ?? candidate.pantry_item_ids?.[0]) === pantryItemId
            ? {
                ...candidate,
                item_name: nextName,
              }
            : candidate,
        ),
      );
      trackEvent("pantry_item_renamed", { category: normalizeCategory(item.category) });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update pantry item.");
    } finally {
      setPendingItemId(null);
    }
  }

  return (
    <div data-testid="pantry-page" className="space-y-4 pb-4">
      <PageHeader
        title="Pantry"
        subtitle="Search, filter, and maintain your pantry inventory."
        right={
          <div className="flex flex-wrap gap-2">
            <Link to="/app/shopping-list" className="pr-btn-secondary inline-flex min-h-10 items-center px-4 text-sm font-semibold">
              Shopping list
            </Link>
            <Link to="/app/ai-insights" className="pr-btn-secondary inline-flex min-h-10 items-center px-4 text-sm font-semibold">
              AI Insights
            </Link>
          </div>
        }
      />

      <Card>
        <div className="grid gap-3 sm:grid-cols-[1.5fr_1fr]">
          <TextField label="Search pantry" value={search} onChange={handleSearchChange} placeholder="Search item or category" />
          <label className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-[var(--pr-text-muted)]">Category</span>
            <select
              value={selectedCategory}
              onChange={(event) => handleCategoryChange(event.target.value)}
              className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={items.length === 0 ? "Pantry is empty" : "No matching pantry items"}
          subtitle={
            items.length === 0
              ? "Scan your pantry to unlock inventory and AI insights."
              : "Try a different search term or category filter."
          }
          image={assetUrl("pantry-inventory-bg.png")}
          action={
            items.length === 0 ? (
              <Link to="/app/scans" className="pr-btn-primary inline-flex min-h-10 items-center px-4 text-sm font-semibold">
                Scan pantry
              </Link>
            ) : null
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => {
            const pantryItemId = item.pantry_item_id ?? item.pantry_item_ids?.[0] ?? item.submission_id;
            const isPending = pendingItemId === pantryItemId;
            return (
              <Card key={`${item.submission_id}-${pantryItemId}`} flat>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold">{item.item_name}</h3>
                    <div className="mt-1 flex flex-wrap gap-2">
                      <Pill label={normalizeCategory(item.category)} />
                      {item.brand ? <Pill label={item.brand} /> : null}
                      {item.fdc_source ? <Pill tone="accent" label={item.fdc_source.toUpperCase()} /> : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {shoppingFeedback && shoppingFeedback.id === pantryItemId ? (
                      <span className="rounded-full border border-[rgba(164,214,76,0.3)] bg-[rgba(164,214,76,0.1)] px-3 py-1 text-xs font-semibold text-[var(--pr-accent)]">
                        {shoppingFeedback.message}
                      </span>
                    ) : null}
                    {item.fdc_source && item.fdc_id ? (
                      <Link
                        to={`/app/explore/${item.fdc_source}/${item.fdc_id}`}
                        className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold"
                      >
                        Nutrition
                      </Link>
                    ) : null}
                    <Button
                      variant="secondary"
                      className="min-h-10 px-3 text-sm"
                      onClick={() => handleAddToShoppingList(item)}
                      disabled={isPending}
                    >
                      Add to list
                    </Button>
                    <Button variant="secondary" className="min-h-10 px-3 text-sm" onClick={() => void handleRename(item)} disabled={isPending}>
                      Rename
                    </Button>
                    <Button
                      variant="ghost"
                      className="min-h-10 border border-[var(--pr-border)] px-3 text-sm text-rose-600"
                      onClick={() => void handleDelete(item)}
                      disabled={isPending}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
